import type { TrialCodeCancelledResponse, TrialCodeEntry, TrialCodeListResponse } from '@league/shared/contract';
import { TRIAL_DAYS } from '@league/shared/entitlements';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { PLAYER_ROW, PlayerTable, QUIET_ACTION, Td, Th, ThActions } from '../../components/PlayerTable';
import { Button, ErrorNote, Field, Input } from '../../components/ui';
import { ApiError, apiFetch, apiPost } from '../../lib/api';

/** Who redeemed it is said only when that is not who she made it for. */
function statusLine(entry: TrialCodeEntry): string {
  const who = entry.redeemedByName && entry.redeemedByName !== entry.recipientName ? `${entry.redeemedByName}: ` : '';
  switch (entry.status) {
    case 'unused': return 'Not used yet';
    case 'in_trial': return `${who}${entry.daysLeft} ${entry.daysLeft === 1 ? 'day' : 'days'} left`;
    case 'ended': return `${who}Trial ended`;
    case 'joined': return `${who}Became a member`;
    case 'cancelled': return 'Cancelled';
  }
}

/**
 * FREE TRIAL CODES (owner, 2026-11-01). The commissioner makes one for a named person and sends it
 * herself: ten days, one league, no card, and one use.
 */
export function TrialCodes() {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [copied, setCopied] = useState<string | null>(null);

  const codes = useQuery({
    queryKey: ['trial-codes'],
    queryFn: () => apiFetch<TrialCodeListResponse>('/api/hq/trial-codes'),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['trial-codes'] });

  const create = useMutation({
    mutationFn: () => apiPost('/api/hq/trial-codes', { recipientName: name.trim() }),
    onSuccess: async () => {
      setName('');
      await refresh();
    },
  });
  const cancel = useMutation({
    mutationFn: (id: string) => apiFetch<TrialCodeCancelledResponse>(`/api/hq/trial-codes/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
  });

  const copy = async (code: string) => {
    await navigator.clipboard.writeText(code);
    setCopied(code);
  };

  const failure = create.error ?? cancel.error;

  return (
    <div className="flex max-w-[56rem] flex-col gap-6 px-6 py-8">
      <h1 className="font-display text-[34px] leading-tight">Trial codes</h1>

      <form
        className="flex flex-wrap items-end gap-3 rounded-md border border-light bg-paper p-4"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate();
        }}
      >
        <div className="min-w-[16rem] flex-1">
          <Field label="Who is it for?" htmlFor="trial-name">
            <Input id="trial-name" required maxLength={120} value={name} onChange={(event) => setName(event.target.value)} />
          </Field>
        </div>
        <Button type="submit" disabled={!name.trim() || create.isPending}>
          {create.isPending ? 'Creating…' : `Create ${TRIAL_DAYS}-day code`}
        </Button>
      </form>

      {failure ? <ErrorNote>{failure instanceof ApiError ? failure.message : 'That did not work. Try again.'}</ErrorNote> : null}
      {codes.isError ? <ErrorNote>Could not load the trial codes.</ErrorNote> : null}

      {codes.data && codes.data.codes.length === 0 ? (
        <p className="rounded-md border border-dashed border-light px-5 py-8 text-center text-[14px] text-ink-3">No trial codes yet.</p>
      ) : null}

      {codes.data && codes.data.codes.length > 0 ? (
        <div className="flex flex-col gap-3">
          <PlayerTable head={<><Th>For</Th><Th>Code</Th><Th>Status</Th><ThActions /></>}>
            {codes.data.codes.map((entry) => (
              <tr key={entry.id} className={`${PLAYER_ROW} hover:bg-chalk`}>
                <Td kind="name" className="text-[15px] font-semibold text-ink">{entry.recipientName}</Td>
                <Td className="tabular whitespace-nowrap text-[14px] text-ink">{entry.code}</Td>
                <Td className="text-[14px] text-ink-2">{statusLine(entry)}</Td>
                <Td kind="actions">
                  <Actions entry={entry} copied={copied} onCopy={copy} onCancel={(id) => cancel.mutate(id)} />
                </Td>
              </tr>
            ))}
          </PlayerTable>

          <ul className="overflow-hidden rounded-[14px] border border-light bg-paper sm:hidden">
            {codes.data.codes.map((entry) => (
              <li key={entry.id} className="border-b border-light px-3.5 py-3.5 last:border-b-0">
                <p className="text-[16px] font-semibold text-ink">{entry.recipientName}</p>
                <p className="tabular mt-0.5 text-[14px] text-ink">{entry.code}</p>
                <p className="mt-0.5 text-[14px] text-ink-2">{statusLine(entry)}</p>
                <div className="mt-2 -ml-2.5"><Actions entry={entry} copied={copied} onCopy={copy} onCancel={(id) => cancel.mutate(id)} /></div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function Actions({ entry, copied, onCopy, onCancel }: {
  entry: TrialCodeEntry;
  copied: string | null;
  onCopy: (code: string) => Promise<void>;
  onCancel: (id: string) => void;
}) {
  return (
    <div className="flex items-center justify-end gap-1 max-sm:justify-start">
      {entry.status === 'unused' ? (
        <button type="button" className={QUIET_ACTION} aria-label={`Copy the code for ${entry.recipientName}`} onClick={() => void onCopy(entry.code)}>
          {copied === entry.code ? 'Copied' : 'Copy'}
        </button>
      ) : null}
      {entry.status === 'unused' || entry.status === 'in_trial' ? (
        <button type="button" className={QUIET_ACTION} aria-label={`Cancel the code for ${entry.recipientName}`} onClick={() => onCancel(entry.id)}>
          Cancel
        </button>
      ) : null}
    </div>
  );
}
